/**
 * MapCommandHandler - AI Command Handler for Map Component
 *
 * Provides comprehensive AI command handling for the MapComponent (map-chart).
 *
 * Features:
 * - View control (zoom, pan, center, fit bounds)
 * - Layer toggle (heatmap, markers, clusters, zones)
 * - Styling (color themes, radius, opacity, intensity)
 * - Filtering (moisture range, zones, dry/wet areas)
 * - Display settings (title, legend, tooltip, controls)
 *
 * Command Categories (50+ patterns):
 * - View Control: zoom in/out, zoom to level, center at coords, fit to data
 * - Layer Toggle: show/hide heatmap/markers/clusters/zones
 * - Styling: themes, colors, radius, opacity, intensity
 * - Filtering: moisture thresholds, zone selection
 * - Display: title, legend, tooltips, navigation controls
 *
 * @see Documents/Prompts/Map_Component_Implementation_Roadmap.md
 */

import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { MAP_COLOR_THEMES } from "@/components/charts/MapComponent.types";
import { logger } from "@/utils/logger";
import type { MapComponentProperties, MapPin, MapGroup } from "@/components/charts/MapComponent.types";

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOptionalRecord(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || isRecord(field);
}

function hasOptionalString(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || typeof field === 'string';
}

function hasOptionalNumber(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || typeof field === 'number';
}

function hasOptionalBoolean(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || typeof field === 'boolean';
}

function hasOptionalArray(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || Array.isArray(field);
}

function isMapComponentProperties(value: Record<string, unknown>): value is MapComponentProperties {
  const layers = value.layers;
  const layerRecordsAreValid = !isRecord(layers) || (
    hasOptionalRecord(layers, 'heatmap') &&
    hasOptionalRecord(layers, 'markers') &&
    hasOptionalRecord(layers, 'clusters') &&
    hasOptionalRecord(layers, 'zones')
  );

  return (
    hasOptionalRecord(value, 'initialView') &&
    hasOptionalNumber(value, 'refreshInterval') &&
    hasOptionalString(value, 'selectedPinId') &&
    hasOptionalString(value, 'colorTheme') &&
    hasOptionalString(value, 'title') &&
    hasOptionalBoolean(value, 'enableClustering') &&
    hasOptionalBoolean(value, 'showLegend') &&
    hasOptionalBoolean(value, 'showTooltip') &&
    hasOptionalBoolean(value, 'enableRealTime') &&
    hasOptionalBoolean(value, 'locationLocked') &&
    hasOptionalRecord(value, 'layers') &&
    layerRecordsAreValid &&
    hasOptionalRecord(value, 'controlsConfig') &&
    hasOptionalRecord(value, 'legendConfig') &&
    hasOptionalRecord(value, 'pinNavigation') &&
    hasOptionalRecord(value, 'pendingPin') &&
    hasOptionalArray(value, 'pins') &&
    hasOptionalArray(value, 'groups') &&
    hasOptionalArray(value, 'moistureRange')
  );
}

// ============================================================================
// Map Theme Presets
// ============================================================================

/**
 * Predefined color themes for map heatmap visualization
 * Each theme provides colors optimized for different use cases
 */
export const MAP_THEMES: Record<string, string> = {
  // Agricultural themes
  agricultural: 'agricultural',
  farm: 'agricultural',
  farming: 'agricultural',
  crop: 'agricultural',
  soil: 'agricultural',

  // Thermal themes
  thermal: 'thermal',
  heat: 'thermal',
  temperature: 'thermal',
  hot: 'thermal',

  // Ocean/water themes
  ocean: 'ocean',
  water: 'ocean',
  sea: 'ocean',
  aqua: 'ocean',
  marine: 'ocean',

  // Grayscale themes
  grayscale: 'grayscale',
  gray: 'grayscale',
  grey: 'grayscale',
  monochrome: 'grayscale',
  bw: 'grayscale',
};

/**
 * Marker size mappings
 */
const MARKER_SIZES: Record<string, 'small' | 'medium' | 'large'> = {
  small: 'small',
  sm: 'small',
  tiny: 'small',
  medium: 'medium',
  md: 'medium',
  normal: 'medium',
  large: 'large',
  lg: 'large',
  big: 'large',
};

type MapPinIcon = NonNullable<MapPin['style']['icon']>;

const MAP_PIN_COMMAND_ICONS: readonly MapPinIcon[] = [
  'location',
  'camera',
  'sensor',
  'power',
  'home',
  'building',
  'warehouse',
  'water',
  'waves',
  'rain',
  'flask',
  'temperature',
  'wind',
  'sun',
  'wifi',
  'radio',
  'flame',
  'warning',
  'maintenance',
  'gauge',
  'info',
];

const MAP_PIN_COMMAND_ICON_SET: ReadonlySet<string> = new Set(MAP_PIN_COMMAND_ICONS);
const MAP_PIN_ICON_ALIASES: Record<string, MapPinIcon> = {
  pin: 'location',
  alert: 'warning',
};

function isMapPinCommandIcon(value: string): value is MapPinIcon {
  return MAP_PIN_COMMAND_ICON_SET.has(value);
}

function resolveMapPinIcon(value: string): MapPinIcon | undefined {
  const normalizedValue = value.toLowerCase();
  if (isMapPinCommandIcon(normalizedValue)) {
    return normalizedValue;
  }
  return MAP_PIN_ICON_ALIASES[normalizedValue];
}

// ============================================================================
// Map Command Handler Class
// ============================================================================

export class MapCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    // Phase 6 responsive patterns MUST come first to have priority over base patterns
    // because patterns like "on tablet zoom in" would otherwise match the base "zoom in" pattern
    this.initializeResponsivePatterns();
    this.initializePatterns();
  }

  private getMapProps(component: Component): MapComponentProperties {
    return this.typedProperties(component, isMapComponentProperties) ?? {};
  }

  private getStyleString(component: Component, key: string): string | undefined {
    const value = this.typedStyle(component)?.[key];
    return typeof value === 'string' ? value : undefined;
  }

  // ============================================================================
  // Phase 6: Responsive Breakpoint Patterns (MUST BE FIRST for priority)
  // ============================================================================

  private initializeResponsivePatterns(): void {
    logger.debug("Initializing MapCommandHandler responsive breakpoint patterns");

    // Pattern: "on mobile X", "on tablet X", "on desktop X"
    this.commandPatterns.set(
      /^on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const subCommand = match[2];
        console.log(`[MapCommandHandler Phase 6] Breakpoint prefix 'on ${breakpoint}' detected`);
        console.log(`[MapCommandHandler Phase 6] Sub-command: "${subCommand}"`);
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "for mobile X", "for tablet X", "for desktop X"
    this.commandPatterns.set(
      /^for\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const subCommand = match[2];
        console.log(`[MapCommandHandler Phase 6] Breakpoint prefix 'for ${breakpoint}' detected`);
        console.log(`[MapCommandHandler Phase 6] Sub-command: "${subCommand}"`);
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "mobile: X", "tablet: X", "desktop: X"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop):\s*(.+)$/i,
      (match, component) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const subCommand = match[2];
        console.log(`[MapCommandHandler Phase 6] Breakpoint prefix '${breakpoint}:' detected`);
        console.log(`[MapCommandHandler Phase 6] Sub-command: "${subCommand}"`);
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "only on mobile X", "only on tablet X", "only on desktop X"
    this.commandPatterns.set(
      /^only\s+on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const subCommand = match[2];
        console.log(`[MapCommandHandler Phase 6] Breakpoint prefix 'only on ${breakpoint}' detected`);
        console.log(`[MapCommandHandler Phase 6] Sub-command: "${subCommand}"`);
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "mobile/tablet/desktop [size]" - e.g., "mobile 200px", "desktop 450px" (for height)
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+(\d+(?:\.\d+)?(?:px|%|vh|rem))$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const height = match[2];
        console.log(`[MapCommandHandler Phase 6] Breakpoint height shorthand '${breakpoint} ${height}' detected`);
        return {
          responsiveStyles: { [breakpoint]: { height } }
        };
      }
    );

    logger.debug("MapCommandHandler responsive breakpoint patterns initialized", {
      patterns: "on X, for X, X:, only on X, X [size]",
    });
  }

  /**
   * Handle a breakpoint-prefixed command by parsing the sub-command
   * and wrapping the result in responsiveStyles
   */
  private handleBreakpointCommand(
    breakpoint: 'mobile' | 'tablet' | 'desktop',
    subCommand: string,
    component: Component
  ): PropertyChange | null {
    const trimmedSubCommand = subCommand.trim();

    console.log(`[MapCommandHandler Phase 6] Breakpoint command detected`);
    console.log(`[MapCommandHandler Phase 6] Breakpoint: ${breakpoint}`);
    console.log(`[MapCommandHandler Phase 6] Sub-command: "${trimmedSubCommand}"`);

    // Parse sub-command using existing patterns (excluding breakpoint patterns)
    const subResult = this.parseSubCommand(trimmedSubCommand, component);

    if (subResult) {
      console.log(`[MapCommandHandler Phase 6] Sub-command parsed successfully`);
      console.log(`[MapCommandHandler Phase 6] Sub-result:`, JSON.stringify(subResult));

      // For style changes, wrap in responsiveStyles
      if (subResult.style && Object.keys(subResult.style).length > 0) {
        console.log(`[MapCommandHandler Phase 6] Wrapping style in responsiveStyles for ${breakpoint}`);
        return {
          responsiveStyles: { [breakpoint]: subResult.style }
        };
      }

      // For property changes that affect styling (like zoom, which could be breakpoint-specific)
      // Return with breakpoint marker so the system knows this is breakpoint-specific
      if (subResult.properties) {
        console.log(`[MapCommandHandler Phase 6] Returning properties with breakpoint marker`);
        return {
          ...subResult,
          breakpoint
        };
      }

      return subResult;
    }

    console.log(`[MapCommandHandler Phase 6] Sub-command not recognized: "${trimmedSubCommand}"`);
    return null;
  }

  /**
   * Parse a sub-command using existing patterns, excluding breakpoint patterns
   * to prevent infinite recursion
   */
  private parseSubCommand(command: string, component: Component): PropertyChange | null {
    const normalizedCommand = command.trim().toLowerCase();

    console.log(`[MapCommandHandler Phase 6] parseSubCommand: "${normalizedCommand}"`);

    // Check if this looks like a nested breakpoint command (prevent recursion)
    const breakpointPrefixPatterns = [
      /^on\s+(mobile|tablet|desktop)\s+/i,
      /^for\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop):\s*/i,
      /^only\s+on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop)\s+\d+/i
    ];

    for (const pattern of breakpointPrefixPatterns) {
      if (pattern.test(normalizedCommand)) {
        console.log(`[MapCommandHandler Phase 6] Nested breakpoint pattern detected, skipping`);
        return null;
      }
    }

    // Match against existing patterns, skipping breakpoint patterns
    for (const [pattern, handler] of this.commandPatterns) {
      // Skip patterns that contain breakpoint references
      const patternSource = pattern.source;
      if (
        patternSource.includes('mobile|tablet|desktop') ||
        (patternSource.includes('on\\s+') && patternSource.includes('mobile'))
      ) {
        continue;
      }

      const match = normalizedCommand.match(pattern);
      if (match) {
        const result = handler(match, component);
        if (result) {
          console.log(`[MapCommandHandler Phase 6] Sub-command matched pattern`);
          return result;
        }
      }
    }

    console.log(`[MapCommandHandler Phase 6] No pattern matched sub-command`);
    return null;
  }

  // ============================================================================
  // Core Map Command Patterns
  // ============================================================================

  private initializePatterns(): void {
    logger.debug("Initializing MapCommandHandler patterns");

    // ========================================================================
    // View Control Patterns (Zoom, Pan, Center, Fit)
    // ========================================================================

    // Zoom in/out patterns
    this.commandPatterns.set(
      /zoom\s*in/i,
      (_match, component) => {
        const currentZoom = this.getMapProps(component)?.initialView?.zoom || 10;
        const newZoom = Math.min(22, currentZoom + 2);
        console.log(`[MapCommandHandler] Zooming in from ${currentZoom} to ${newZoom}`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              zoom: newZoom,
            },
          },
        };
      }
    );

    this.commandPatterns.set(
      /zoom\s*out/i,
      (_match, component) => {
        const currentZoom = this.getMapProps(component)?.initialView?.zoom || 10;
        const newZoom = Math.max(0, currentZoom - 2);
        console.log(`[MapCommandHandler] Zooming out from ${currentZoom} to ${newZoom}`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              zoom: newZoom,
            },
          },
        };
      }
    );

    // Min/max zoom patterns (must be checked before general zoom patterns)
    this.commandPatterns.set(
      /(?:set\s+)?min(?:imum)?\s+zoom\s+(?:to\s+)?(\d+)/i,
      (match) => {
        const minZoom = Math.max(0, Math.min(22, parseInt(match[1], 10)));
        console.log(`[MapCommandHandler] Setting minimum zoom to ${minZoom}`);
        return { properties: { minZoom } };
      }
    );

    this.commandPatterns.set(
      /(?:set\s+)?max(?:imum)?\s+zoom\s+(?:to\s+)?(\d+)/i,
      (match) => {
        const maxZoom = Math.max(0, Math.min(22, parseInt(match[1], 10)));
        console.log(`[MapCommandHandler] Setting maximum zoom to ${maxZoom}`);
        return { properties: { maxZoom } };
      }
    );

    // Zoom to specific level (excluding min/max patterns via negative lookahead)
    this.commandPatterns.set(
      /(?:set\s+)?(?!min|max)zoom\s+(?:to\s+)?(\d+(?:\.\d+)?)/i,
      (match, component) => {
        const zoom = Math.min(22, Math.max(0, parseFloat(match[1])));
        console.log(`[MapCommandHandler] Setting zoom to ${zoom}`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              zoom,
            },
          },
        };
      }
    );

    // Zoom level X
    this.commandPatterns.set(
      /zoom\s+level\s+(\d+(?:\.\d+)?)/i,
      (match, component) => {
        const zoom = Math.min(22, Math.max(0, parseFloat(match[1])));
        console.log(`[MapCommandHandler] Setting zoom level to ${zoom}`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              zoom,
            },
          },
        };
      }
    );

    // Center at coordinates
    this.commandPatterns.set(
      /center\s+(?:at\s+)?(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/i,
      (match, component) => {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        console.log(`[MapCommandHandler] Centering map at [${lng}, ${lat}]`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              center: [lng, lat],
            },
          },
        };
      }
    );

    // Fit to all data/sensors
    this.commandPatterns.set(
      /fit\s+(?:to\s+)?(?:all\s+)?(?:data|sensors|bounds|extent)/i,
      () => {
        console.log("[MapCommandHandler] Fit to data bounds");
        return {
          properties: {
            _action: 'fitToBounds',
            _actionTimestamp: Date.now(),
          },
        };
      }
    );

    // Set pitch/tilt: "set pitch 45", "tilt map 45", "tilt to 60"
    this.commandPatterns.set(
      /(?:set\s+)?(?:pitch|tilt)\s+(?:(?:the\s+)?map\s+)?(?:to\s+)?(\d+)/i,
      (match, component) => {
        const pitch = Math.min(85, Math.max(0, parseInt(match[1], 10)));
        console.log(`[MapCommandHandler] Setting pitch/tilt to ${pitch}`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              pitch,
            },
          },
        };
      }
    );

    // Set bearing/rotation
    this.commandPatterns.set(
      /(?:set\s+)?(?:bearing|rotation)\s+(?:to\s+)?(-?\d+)/i,
      (match, component) => {
        const bearing = parseInt(match[1], 10) % 360;
        console.log(`[MapCommandHandler] Setting bearing to ${bearing}`);
        return {
          properties: {
            initialView: {
              ...this.getMapProps(component)?.initialView,
              bearing,
            },
          },
        };
      }
    );

    // Reset view
    this.commandPatterns.set(
      /reset\s+(?:map\s+)?view/i,
      () => {
        console.log("[MapCommandHandler] Resetting view to defaults");
        return {
          properties: {
            initialView: {
              center: [-98.5, 39.8],
              zoom: 4,
              pitch: 0,
              bearing: 0,
            },
          },
        };
      }
    );

    // ========================================================================
    // Layer Toggle Patterns
    // ========================================================================

    // Show/hide heatmap
    this.commandPatterns.set(
      /(show|hide|enable|disable|toggle)\s+(?:the\s+)?heat\s*map/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const enabled = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${enabled ? 'Showing' : 'Hiding'} heatmap layer`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                enabled,
              },
            },
            activeLayer: enabled ? 'heatmap' : undefined,
          },
        };
      }
    );

    // Show/hide markers
    this.commandPatterns.set(
      /(show|hide|enable|disable|toggle)\s+(?:the\s+)?markers?/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const enabled = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${enabled ? 'Showing' : 'Hiding'} markers layer`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              markers: {
                ...this.getMapProps(component)?.layers?.markers,
                enabled,
              },
            },
            activeLayer: enabled ? 'markers' : undefined,
          },
        };
      }
    );

    // Show/hide clusters
    this.commandPatterns.set(
      /(show|hide|enable|disable|toggle)\s+(?:the\s+)?cluster(?:s|ing)?/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const enabled = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${enabled ? 'Enabling' : 'Disabling'} clustering`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              clusters: {
                ...this.getMapProps(component)?.layers?.clusters,
                enabled,
              },
            },
            enableClustering: enabled,
          },
        };
      }
    );

    // Show/hide zones layer (must be followed by "layer" or "overlay" or nothing else)
    // NOTE: We use a more specific pattern to avoid matching "show zones A, B" which is for filtering
    this.commandPatterns.set(
      /(show|hide|enable|disable|toggle)\s+(?:the\s+)?(?:field\s+)?zones?\s*(?:layer|overlay)?$/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const enabled = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${enabled ? 'Showing' : 'Hiding'} zones layer`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              zones: {
                ...this.getMapProps(component)?.layers?.zones,
                enabled,
              },
            },
          },
        };
      }
    );

    // Show marker labels
    this.commandPatterns.set(
      /(show|hide)\s+(?:marker\s+)?labels?/i,
      (match, component) => {
        const showLabels = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${showLabels ? 'Showing' : 'Hiding'} marker labels`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              markers: {
                ...this.getMapProps(component)?.layers?.markers,
                showLabels,
              },
            },
          },
        };
      }
    );

    // Switch to layer mode
    this.commandPatterns.set(
      /(?:switch\s+to|use|activate)\s+(heatmap|markers?|clusters?)/i,
      (match, component) => {
        const layer = match[1].toLowerCase().replace(/s$/, '');
        const activeLayer = layer === 'marker' ? 'markers' : layer === 'cluster' ? 'clusters' : layer;
        console.log(`[MapCommandHandler] Switching to ${activeLayer} mode`);

        const layers = { ...this.getMapProps(component)?.layers };
        // Disable all, enable selected
        if (layers.heatmap) layers.heatmap = { ...layers.heatmap, enabled: activeLayer === 'heatmap' };
        if (layers.markers) layers.markers = { ...layers.markers, enabled: activeLayer === 'markers' };
        if (layers.clusters) layers.clusters = { ...layers.clusters, enabled: activeLayer === 'clusters' };

        return {
          properties: {
            layers,
            activeLayer: activeLayer as 'heatmap' | 'markers' | 'clusters',
          },
        };
      }
    );

    // ========================================================================
    // Styling Patterns (Themes, Colors, Radius, Opacity)
    // ========================================================================

    // Apply theme
    this.commandPatterns.set(
      /(?:use|apply|set)\s+(?:the\s+)?(\w+)\s+(?:color\s+)?theme/i,
      (match, component) => {
        const themeName = match[1].toLowerCase();
        const resolvedTheme = MAP_THEMES[themeName] || themeName;

        if (MAP_COLOR_THEMES[resolvedTheme]) {
          console.log(`[MapCommandHandler] Applying theme: ${resolvedTheme}`);
          return {
            properties: {
              colorTheme: resolvedTheme,
              layers: {
                ...this.getMapProps(component)?.layers,
                heatmap: {
                  ...this.getMapProps(component)?.layers?.heatmap,
                  colorScheme: resolvedTheme as 'agricultural' | 'thermal' | 'ocean' | 'grayscale',
                },
              },
            },
          };
        }
        console.log(`[MapCommandHandler] Theme not found: ${themeName}`);
        return null;
      }
    );

    // Theme keywords without "theme"
    this.commandPatterns.set(
      /(?:use|apply)\s+(agricultural|thermal|ocean|grayscale|farm|water|heat)\s+colou?rs?/i,
      (match, component) => {
        const themeName = match[1].toLowerCase();
        const resolvedTheme = MAP_THEMES[themeName] || themeName;
        console.log(`[MapCommandHandler] Applying colors: ${resolvedTheme}`);
        return {
          properties: {
            colorTheme: resolvedTheme,
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                colorScheme: resolvedTheme as 'agricultural' | 'thermal' | 'ocean' | 'grayscale',
              },
            },
          },
        };
      }
    );

    // Set cluster radius (must be checked BEFORE generic radius pattern)
    this.commandPatterns.set(
      /(?:set\s+)?cluster\s+radius\s+(?:to\s+)?(\d+)/i,
      (match, component) => {
        const radius = Math.min(200, Math.max(20, parseInt(match[1], 10)));
        console.log(`[MapCommandHandler] Setting cluster radius to ${radius}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              clusters: {
                ...this.getMapProps(component)?.layers?.clusters,
                radius,
              },
            },
          },
        };
      }
    );

    // Set heatmap radius (generic "radius" also maps to heatmap)
    // Supports: "set heatmap radius 30", "set heat map radius to 25", "radius 20"
    this.commandPatterns.set(
      /(?:set\s+)?(?:heat\s*map\s+)?radius\s+(?:to\s+)?(\d+)/i,
      (match, component) => {
        const radius = Math.min(100, Math.max(5, parseInt(match[1], 10)));
        console.log(`[MapCommandHandler] Setting heatmap radius to ${radius}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                radius,
              },
            },
          },
        };
      }
    );

    // Increase/decrease radius
    // Supports: "increase heatmap radius", "decrease heat map radius", "bigger radius"
    this.commandPatterns.set(
      /(increase|decrease|bigger|smaller)\s+(?:heat\s*map\s+)?radius/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const currentRadius = this.getMapProps(component)?.layers?.heatmap?.radius || 20;
        const delta = ['increase', 'bigger'].includes(action) ? 5 : -5;
        const radius = Math.min(100, Math.max(5, currentRadius + delta));
        console.log(`[MapCommandHandler] Adjusting radius from ${currentRadius} to ${radius}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                radius,
              },
            },
          },
        };
      }
    );

    // Set opacity (accepts decimals like 0.5, .1 or percentages like 50%)
    // Supports: "set heatmap opacity 0.5", "set heat map opacity to .1", "opacity 50%"
    this.commandPatterns.set(
      /(?:set\s+)?(?:heat\s*map\s+)?opacity\s+(?:to\s+)?(\d*\.?\d+)%?/i,
      (match, component) => {
        let opacity = parseFloat(match[1]);
        opacity = opacity > 1 ? opacity / 100 : opacity;
        opacity = Math.min(1, Math.max(0, opacity));
        console.log(`[MapCommandHandler] Setting heatmap opacity to ${opacity}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                opacity,
              },
            },
          },
        };
      }
    );

    // More/less transparent
    // Supports: "make heatmap transparent", "make heat map more opaque", "more transparent"
    this.commandPatterns.set(
      /(?:make\s+)?(?:heat\s*map\s+)?(?:more\s+)?(transparent|opaque)/i,
      (match, component) => {
        const currentOpacity = this.getMapProps(component)?.layers?.heatmap?.opacity || 0.8;
        const opacity = match[1].toLowerCase() === 'transparent'
          ? Math.max(0.1, currentOpacity - 0.2)
          : Math.min(1, currentOpacity + 0.2);
        console.log(`[MapCommandHandler] Adjusting opacity to ${opacity}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                opacity,
              },
            },
          },
        };
      }
    );

    // Set intensity
    // Supports: "set heatmap intensity 1.5", "set heat map intensity to 2", "intensity 0.5"
    this.commandPatterns.set(
      /(?:set\s+)?(?:heat\s*map\s+)?intensity\s+(?:to\s+)?(\d+(?:\.\d+)?)/i,
      (match, component) => {
        const intensity = Math.min(3, Math.max(0.1, parseFloat(match[1])));
        console.log(`[MapCommandHandler] Setting heatmap intensity to ${intensity}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              heatmap: {
                ...this.getMapProps(component)?.layers?.heatmap,
                intensity,
              },
            },
          },
        };
      }
    );

    // Set marker size
    this.commandPatterns.set(
      /(?:set\s+)?marker\s+size\s+(?:to\s+)?(\w+)/i,
      (match, component) => {
        const sizeInput = match[1].toLowerCase();
        const size = MARKER_SIZES[sizeInput] || 'medium';
        console.log(`[MapCommandHandler] Setting marker size to ${size}`);
        return {
          properties: {
            layers: {
              ...this.getMapProps(component)?.layers,
              markers: {
                ...this.getMapProps(component)?.layers?.markers,
                size,
              },
            },
          },
        };
      }
    );

    // NOTE: Cluster radius pattern moved to styling section (before heatmap radius)

    // ========================================================================
    // Filtering Patterns
    // ========================================================================

    // Filter by moisture range
    this.commandPatterns.set(
      /(?:filter\s+)?moisture\s+(?:between\s+)?(\d+)\s*(?:to|-)\s*(\d+)/i,
      (match) => {
        const min = Math.max(0, parseInt(match[1], 10));
        const max = Math.min(100, parseInt(match[2], 10));
        console.log(`[MapCommandHandler] Setting moisture filter: ${min}-${max}%`);
        return {
          properties: {
            moistureRange: [min, max] as [number, number],
          },
        };
      }
    );

    // Moisture below/under threshold
    this.commandPatterns.set(
      /(?:show\s+)?moisture\s+(?:below|under|<)\s*(\d+)/i,
      (match) => {
        const max = Math.min(100, parseInt(match[1], 10));
        console.log(`[MapCommandHandler] Filtering moisture below ${max}%`);
        return {
          properties: {
            moistureRange: [0, max] as [number, number],
          },
        };
      }
    );

    // Moisture above/over threshold
    this.commandPatterns.set(
      /(?:show\s+)?moisture\s+(?:above|over|>)\s*(\d+)/i,
      (match) => {
        const min = Math.max(0, parseInt(match[1], 10));
        console.log(`[MapCommandHandler] Filtering moisture above ${min}%`);
        return {
          properties: {
            moistureRange: [min, 100] as [number, number],
          },
        };
      }
    );

    // Show dry areas
    this.commandPatterns.set(
      /show\s+(?:only\s+)?dry\s+(?:areas?|sensors?|zones?)/i,
      () => {
        console.log("[MapCommandHandler] Filtering to dry areas (0-30%)");
        return {
          properties: {
            moistureRange: [0, 30] as [number, number],
          },
        };
      }
    );

    // Show wet/saturated areas
    this.commandPatterns.set(
      /show\s+(?:only\s+)?(?:wet|saturated)\s+(?:areas?|sensors?|zones?)/i,
      () => {
        console.log("[MapCommandHandler] Filtering to wet areas (70-100%)");
        return {
          properties: {
            moistureRange: [70, 100] as [number, number],
          },
        };
      }
    );

    // Show optimal moisture
    this.commandPatterns.set(
      /show\s+(?:only\s+)?optimal\s+(?:moisture|areas?|sensors?)/i,
      () => {
        console.log("[MapCommandHandler] Filtering to optimal moisture (30-70%)");
        return {
          properties: {
            moistureRange: [30, 70] as [number, number],
          },
        };
      }
    );

    // Filter to specific zone
    this.commandPatterns.set(
      /(?:filter\s+to|show\s+(?:only\s+)?)?zone\s+([A-Za-z0-9]+)/i,
      (match) => {
        const zone = match[1].toUpperCase();
        console.log(`[MapCommandHandler] Filtering to zone ${zone}`);
        return {
          properties: {
            selectedZones: [zone],
          },
        };
      }
    );

    // Filter to multiple zones
    this.commandPatterns.set(
      /(?:filter\s+to|show)\s+zones?\s+([A-Za-z0-9,\s]+)/i,
      (match) => {
        const zones = match[1].split(/[,\s]+/).map(z => z.trim().toUpperCase()).filter(Boolean);
        console.log(`[MapCommandHandler] Filtering to zones: ${zones.join(', ')}`);
        return {
          properties: {
            selectedZones: zones,
          },
        };
      }
    );

    // Clear filters
    this.commandPatterns.set(
      /(?:clear|reset|remove)\s+(?:all\s+)?filters?/i,
      () => {
        console.log("[MapCommandHandler] Clearing all filters");
        return {
          properties: {
            moistureRange: undefined,
            selectedZones: undefined,
            dateRange: undefined,
          },
        };
      }
    );

    // Show all sensors
    this.commandPatterns.set(
      /show\s+all\s+(?:sensors?|data|points?)/i,
      () => {
        console.log("[MapCommandHandler] Showing all sensors (clearing filters)");
        return {
          properties: {
            moistureRange: undefined,
            selectedZones: undefined,
          },
        };
      }
    );

    // ========================================================================
    // Display Settings Patterns
    // ========================================================================

    // Set title
    this.commandPatterns.set(
      /(?:set\s+)?(?:map\s+)?title\s+(?:to\s+)?["']([^"']+)["']/i,
      (match) => {
        const title = match[1];
        console.log(`[MapCommandHandler] Setting title to: ${title}`);
        return { properties: { title } };
      }
    );

    // Title without quotes
    this.commandPatterns.set(
      /(?:set\s+)?(?:map\s+)?title\s+(?:to\s+)?(.+)/i,
      (match) => {
        const title = match[1].trim().replace(/^["']|["']$/g, '');
        console.log(`[MapCommandHandler] Setting title to: ${title}`);
        return { properties: { title } };
      }
    );

    // Show/hide legend
    this.commandPatterns.set(
      /(show|hide|enable|disable)\s+(?:the\s+)?legend/i,
      (match) => {
        const action = match[1].toLowerCase();
        const showLegend = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${showLegend ? 'Showing' : 'Hiding'} legend`);
        return { properties: { showLegend } };
      }
    );

    // Show/hide tooltips
    this.commandPatterns.set(
      /(show|hide|enable|disable)\s+(?:the\s+)?tooltips?/i,
      (match) => {
        const action = match[1].toLowerCase();
        const showTooltip = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${showTooltip ? 'Enabling' : 'Disabling'} tooltips`);
        return { properties: { showTooltip } };
      }
    );

    // Show/hide controls
    this.commandPatterns.set(
      /(show|hide|enable|disable)\s+(?:the\s+)?(?:navigation\s+)?controls?/i,
      (match) => {
        const action = match[1].toLowerCase();
        const showControls = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${showControls ? 'Showing' : 'Hiding'} controls`);
        return { properties: { showControls } };
      }
    );

    // Set description
    this.commandPatterns.set(
      /(?:set\s+)?description\s+(?:to\s+)?["']([^"']+)["']/i,
      (match) => {
        const description = match[1];
        console.log(`[MapCommandHandler] Setting description`);
        return { properties: { description } };
      }
    );

    // Enable/disable real-time updates
    this.commandPatterns.set(
      /(enable|disable)\s+(?:real[\s-]?time|live)\s+(?:updates?|data)/i,
      (match) => {
        const enableRealTime = match[1].toLowerCase() === 'enable';
        console.log(`[MapCommandHandler] ${enableRealTime ? 'Enabling' : 'Disabling'} real-time updates`);
        return { properties: { enableRealTime } };
      }
    );

    // Set refresh interval
    this.commandPatterns.set(
      /(?:set\s+)?refresh\s+(?:interval\s+)?(?:to\s+)?(\d+)\s*(?:s|sec|seconds?)?/i,
      (match) => {
        const refreshInterval = Math.max(5, parseInt(match[1], 10));
        console.log(`[MapCommandHandler] Setting refresh interval to ${refreshInterval}s`);
        return { properties: { refreshInterval } };
      }
    );

    // NOTE: min/max zoom patterns moved to beginning of view control section

    // ========================================================================
    // Map Type/Provider Patterns
    // ========================================================================

    // Change tile provider
    this.commandPatterns.set(
      /(?:use|switch\s+to)\s+(maptiler|stadia|osm|openstreetmap)\s+(?:tiles?|base\s*map)?/i,
      (match) => {
        let tileProvider = match[1].toLowerCase();
        if (tileProvider === 'openstreetmap') tileProvider = 'osm';
        console.log(`[MapCommandHandler] Switching to ${tileProvider} tiles`);
        return { properties: { tileProvider: tileProvider as 'maptiler' | 'stadia' | 'osm' } };
      }
    );

    // Change map style (satellite, streets, hybrid)
    // Satellite/hybrid automatically switches to maptiler provider (requires API key)
    this.commandPatterns.set(
      /(?:satellite|streets?|hybrid)\s*(?:view|mode|map)?$/i,
      (match) => {
        const input = match[0].toLowerCase();
        let mapStyle: 'satellite' | 'streets' | 'hybrid' = 'streets';
        if (input.includes('satellite')) mapStyle = 'satellite';
        else if (input.includes('hybrid')) mapStyle = 'hybrid';
        else if (input.includes('street')) mapStyle = 'streets';

        // Satellite and hybrid require maptiler provider
        const needsMaptiler = mapStyle === 'satellite' || mapStyle === 'hybrid';
        console.log(`[MapCommandHandler] Changing map style to ${mapStyle}${needsMaptiler ? ' (switching to maptiler)' : ''}`);

        if (needsMaptiler) {
          return { properties: { mapStyle, tileProvider: 'maptiler' } };
        }
        return { properties: { mapStyle } };
      }
    );

    // Show satellite/streets/hybrid
    this.commandPatterns.set(
      /(?:show|use|switch\s+to)\s+(?:the\s+)?(satellite|streets?|hybrid)(?:\s+(?:view|map|mode))?/i,
      (match) => {
        const input = match[1].toLowerCase();
        let mapStyle: 'satellite' | 'streets' | 'hybrid' = 'streets';
        if (input.includes('satellite')) mapStyle = 'satellite';
        else if (input.includes('hybrid')) mapStyle = 'hybrid';
        else if (input.includes('street')) mapStyle = 'streets';

        // Satellite and hybrid require maptiler provider
        const needsMaptiler = mapStyle === 'satellite' || mapStyle === 'hybrid';
        console.log(`[MapCommandHandler] Switching to ${mapStyle} view${needsMaptiler ? ' (using maptiler)' : ''}`);

        if (needsMaptiler) {
          return { properties: { mapStyle, tileProvider: 'maptiler' } };
        }
        return { properties: { mapStyle } };
      }
    );

    // ========================================================================
    // Data Operations Patterns
    // ========================================================================

    // Refresh data - simple refresh
    this.commandPatterns.set(
      /(?:refresh|update|reload)\s+(?:sensor\s+)?data/i,
      () => {
        console.log("[MapCommandHandler] Triggering data refresh");
        return {
          properties: {
            _action: 'refreshData',
          },
        };
      }
    );

    // Refresh every X seconds/minutes (Phase 4)
    this.commandPatterns.set(
      /(?:refresh|update)\s+every\s+(\d+)\s*(second|minute|sec|min)s?/i,
      (match) => {
        let seconds = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();
        if (unit.startsWith('min')) {
          seconds *= 60;
        }
        // Minimum 5 seconds, maximum 3600 seconds (1 hour)
        seconds = Math.max(5, Math.min(3600, seconds));
        console.log(`[MapCommandHandler] Setting refresh interval to ${seconds} seconds`);
        return {
          properties: {
            refreshInterval: seconds,
          },
        };
      }
    );

    // Enable real-time updates (Phase 4)
    this.commandPatterns.set(
      /(?:enable|turn on|activate|start)\s+(?:real[\s-]?time|live)\s*(?:updates?|mode)?/i,
      () => {
        console.log("[MapCommandHandler] Enabling real-time updates");
        return {
          properties: {
            enableRealTime: true,
            showConnectionStatus: true,
          },
        };
      }
    );

    // Disable real-time updates (Phase 4)
    this.commandPatterns.set(
      /(?:disable|turn off|deactivate|stop)\s+(?:real[\s-]?time|live)\s*(?:updates?|mode)?/i,
      () => {
        console.log("[MapCommandHandler] Disabling real-time updates");
        return {
          properties: {
            enableRealTime: false,
          },
        };
      }
    );

    // Pause/resume updates (Phase 4)
    this.commandPatterns.set(
      /(pause|resume)\s+(?:data\s+)?updates?/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const isPausing = action === 'pause';
        console.log(`[MapCommandHandler] ${isPausing ? 'Pausing' : 'Resuming'} updates`);
        return {
          properties: {
            enableRealTime: !isPausing,
            refreshInterval: isPausing ? 0 : this.getMapProps(component)?.refreshInterval || 30,
          },
        };
      }
    );

    // Show/hide connection status (Phase 4)
    this.commandPatterns.set(
      /(show|hide)\s+(?:connection\s+)?status/i,
      (match) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} connection status`);
        return {
          properties: {
            showConnectionStatus: show,
          },
        };
      }
    );

    // Stop auto-refresh (Phase 4)
    this.commandPatterns.set(
      /(?:stop|disable|turn off)\s+(?:auto[\s-]?)?refresh/i,
      () => {
        console.log("[MapCommandHandler] Disabling auto-refresh");
        return {
          properties: {
            refreshInterval: 0,
          },
        };
      }
    );

    // Generate sample data
    this.commandPatterns.set(
      /(?:generate|add|load)\s+(?:sample|test|demo)\s+data/i,
      () => {
        console.log("[MapCommandHandler] Loading sample data");
        return {
          properties: {
            _action: 'loadSampleData',
          },
        };
      }
    );

    // ========================================================================
    // Phase 5: Map Controls & Legend Patterns
    // ========================================================================

    // Show/hide all controls
    this.commandPatterns.set(
      /(show|hide|enable|disable)\s+(?:the\s+)?(?:all\s+)?(?:map\s+)?controls?$/i,
      (match) => {
        const action = match[1].toLowerCase();
        const showControls = ['show', 'enable'].includes(action);
        console.log(`[MapCommandHandler] ${showControls ? 'Showing' : 'Hiding'} map controls`);
        return { properties: { showControls } };
      }
    );

    // Show/hide zoom controls
    this.commandPatterns.set(
      /(show|hide)\s+(?:the\s+)?zoom\s+(?:controls?|buttons?)/i,
      (match, component) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} zoom controls`);
        return {
          properties: {
            controlsConfig: {
              ...this.getMapProps(component)?.controlsConfig,
              showZoom: show,
            },
          },
        };
      }
    );

    // Show/hide compass
    this.commandPatterns.set(
      /(show|hide)\s+compass/i,
      (match, component) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} compass`);
        return {
          properties: {
            controlsConfig: {
              ...this.getMapProps(component)?.controlsConfig,
              showCompass: show,
            },
          },
        };
      }
    );

    // Show/hide scale
    this.commandPatterns.set(
      /(show|hide)\s+scale(?:\s+(?:bar|indicator))?/i,
      (match, component) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} scale indicator`);
        return {
          properties: {
            controlsConfig: {
              ...this.getMapProps(component)?.controlsConfig,
              showScale: show,
            },
          },
        };
      }
    );

    // Move controls to position: "move controls to top-right", "move controls to top right"
    this.commandPatterns.set(
      /(?:move|position)\s+(?:the\s+)?controls?\s+(?:to\s+)?(?:the\s+)?(top[- ]?left|top[- ]?right|bottom[- ]?left|bottom[- ]?right)/i,
      (match, component) => {
        // Normalize position to use hyphens (e.g., "top right" -> "top-right")
        const position = match[1].toLowerCase().replace(' ', '-') as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
        console.log(`[MapCommandHandler] Moving controls to ${position}`);
        return {
          properties: {
            controlsConfig: {
              ...this.getMapProps(component)?.controlsConfig,
              position,
            },
          },
        };
      }
    );

    // Fullscreen toggle
    this.commandPatterns.set(
      /(?:enter|exit|toggle)\s+fullscreen/i,
      () => {
        console.log("[MapCommandHandler] Triggering fullscreen toggle");
        return {
          properties: {
            _action: 'toggleFullscreen',
          },
        };
      }
    );

    // Fit to data / Reset view
    this.commandPatterns.set(
      /(?:fit\s+to\s+(?:all\s+)?(?:data|sensors?|bounds?)|reset\s+(?:map\s+)?view)/i,
      () => {
        console.log("[MapCommandHandler] Fit to data bounds / Reset view");
        return {
          properties: {
            _action: 'fitToBounds',
            _actionTimestamp: Date.now(),
          },
        };
      }
    );

    // Expand/collapse legend
    this.commandPatterns.set(
      /(expand|collapse|minimize|maximize)\s+(?:the\s+)?legend/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const expanded = ['expand', 'maximize'].includes(action);
        console.log(`[MapCommandHandler] ${expanded ? 'Expanding' : 'Collapsing'} legend`);
        return {
          properties: {
            legendConfig: {
              ...this.getMapProps(component)?.legendConfig,
              expanded,
            },
          },
        };
      }
    );

    // Move legend to position: "move legend to top-right", "move legend to top right"
    this.commandPatterns.set(
      /(?:move|position)\s+(?:the\s+)?legend\s+(?:to\s+)?(?:the\s+)?(top[- ]?left|top[- ]?right|bottom[- ]?left|bottom[- ]?right)/i,
      (match, component) => {
        // Normalize position to use hyphens (e.g., "top right" -> "top-right")
        const position = match[1].toLowerCase().replace(' ', '-') as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
        console.log(`[MapCommandHandler] Moving legend to ${position}`);
        return {
          properties: {
            legendConfig: {
              ...this.getMapProps(component)?.legendConfig,
              position,
            },
          },
        };
      }
    );

    // Show/hide layer toggles in legend
    this.commandPatterns.set(
      /(show|hide)\s+layer\s+toggles?(?:\s+in\s+legend)?/i,
      (match, component) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} layer toggles in legend`);
        return {
          properties: {
            legendConfig: {
              ...this.getMapProps(component)?.legendConfig,
              showLayerToggles: show,
            },
          },
        };
      }
    );

    // Show/hide sensor count in legend
    this.commandPatterns.set(
      /(show|hide)\s+sensor\s+count(?:\s+in\s+legend)?/i,
      (match, component) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} sensor count in legend`);
        return {
          properties: {
            legendConfig: {
              ...this.getMapProps(component)?.legendConfig,
              showSensorCount: show,
            },
          },
        };
      }
    );

    // Show/hide last updated in legend
    this.commandPatterns.set(
      /(show|hide)\s+(?:last\s+)?updated(?:\s+time(?:stamp)?)?(?:\s+in\s+legend)?/i,
      (match, component) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} last updated in legend`);
        return {
          properties: {
            legendConfig: {
              ...this.getMapProps(component)?.legendConfig,
              showLastUpdated: show,
            },
          },
        };
      }
    );

    // Compact mode
    this.commandPatterns.set(
      /(?:enable|use)\s+compact\s+(?:mode|controls?)/i,
      (match, component) => {
        console.log("[MapCommandHandler] Enabling compact mode");
        return {
          properties: {
            controlsConfig: {
              ...this.getMapProps(component)?.controlsConfig,
              compact: true,
            },
          },
        };
      }
    );

    this.commandPatterns.set(
      /(?:disable|exit)\s+compact\s+(?:mode|controls?)/i,
      (match, component) => {
        console.log("[MapCommandHandler] Disabling compact mode");
        return {
          properties: {
            controlsConfig: {
              ...this.getMapProps(component)?.controlsConfig,
              compact: false,
            },
          },
        };
      }
    );

    // ========================================================================
    // Location Lock Patterns
    // ========================================================================

    // Set/lock location: "set location", "lock location", "save this view", "lock this view"
    // This locks the current view and disables auto-sync
    // Dispatches an action so MapComponent captures the live viewport into initialView
    this.commandPatterns.set(
      /(?:set|lock|save)(?:\s+(?:this|current|the|map))?\s+(?:location|view|position)$/i,
      () => {
        console.log("[MapCommandHandler] Locking current location - dispatching lockCurrentView action");
        return {
          properties: {
            locationLocked: true,
            _action: 'lockCurrentView',
            _actionTimestamp: Date.now(),
          },
        };
      }
    );

    // Unlock location: "unlock location", "unlock view", "resume auto-sync", "resume sync"
    // This resumes auto-sync of viewport state
    this.commandPatterns.set(
      /(?:unlock)(?:\s+(?:the|map))?\s+(?:location|view|position)|resume\s+(?:auto[- ]?)?sync/i,
      () => {
        console.log("[MapCommandHandler] Unlocking location - resuming auto-sync");
        return {
          properties: {
            locationLocked: false,
          },
        };
      }
    );

    // Simple "lock" command for maps - sets locationLocked
    // This handles the common case where user just types "lock" without specifying what
    // Dispatches an action so MapComponent captures the live viewport into initialView
    this.commandPatterns.set(
      /^lock$/i,
      () => {
        console.log("[MapCommandHandler] Simple lock - dispatching lockCurrentView action");
        return {
          properties: {
            locationLocked: true,
            _action: 'lockCurrentView',
            _actionTimestamp: Date.now(),
          },
        };
      }
    );

    // Simple "unlock" command for maps - clears locationLocked
    // This handles the common case where user just types "unlock" without specifying what
    this.commandPatterns.set(
      /^unlock$/i,
      () => {
        console.log("[MapCommandHandler] Simple unlock - resuming auto-sync");
        return {
          properties: {
            locationLocked: false,
          },
        };
      }
    );

    // ========================================================================
    // Phase 5 (Groups/Pins): Group/Layer Management Patterns
    // ========================================================================

    // Create group/layer: "create group Field A", "create layer Water Sensors"
    this.commandPatterns.set(
      /(?:create|add|new)\s+(?:group|layer)\s+(?:named?\s+)?["']?([^"']+)["']?$/i,
      (match) => {
        const groupName = match[1].trim();
        console.log(`[MapCommandHandler] Creating group: ${groupName}`);
        return {
          properties: {
            _action: 'createGroup',
            _actionTimestamp: Date.now(),
            _groupName: groupName,
          },
        };
      }
    );

    // Create sensor-type group/layer: "create moisture sensor group", "create moisture sensor layer"
    this.commandPatterns.set(
      /(?:create|add|new)\s+(\w+)\s+sensor\s+(?:group|layer)/i,
      (match) => {
        const sensorType = match[1].toLowerCase();
        console.log(`[MapCommandHandler] Creating sensor-type group: ${sensorType}`);
        return {
          properties: {
            _action: 'createGroup',
            _actionTimestamp: Date.now(),
            _groupType: 'sensor-type',
            _sensorType: sensorType,
          },
        };
      }
    );

    // Create area group/layer: "create area group North Field", "create area layer Zone A"
    this.commandPatterns.set(
      /(?:create|add|new)\s+area\s+(?:group|layer)\s+(?:named?\s+)?["']?([^"']+)["']?$/i,
      (match) => {
        const groupName = match[1].trim();
        console.log(`[MapCommandHandler] Creating area group: ${groupName}`);
        return {
          properties: {
            _action: 'createAreaGroup',
            _actionTimestamp: Date.now(),
            _groupName: groupName,
          },
        };
      }
    );

    // Show/hide group/layer: "show group Field A", "show layer Water Sensors", "hide layer Test"
    this.commandPatterns.set(
      /(show|hide)\s+(?:group|layer)\s+["']?([^"']+)["']?$/i,
      (match) => {
        const action = match[1].toLowerCase();
        const groupName = match[2].trim();
        const visible = action === 'show';
        console.log(`[MapCommandHandler] ${visible ? 'Showing' : 'Hiding'} group: ${groupName}`);
        return {
          properties: {
            _action: 'setGroupVisibility',
            _actionTimestamp: Date.now(),
            _groupName: groupName,
            _visible: visible,
          },
        };
      }
    );

    // Filter to group/layer: "filter to group Field A", "filter to layer Water Sensors"
    this.commandPatterns.set(
      /(?:filter\s+to|show\s+only)\s+(?:group|layer)\s+["']?([^"']+)["']?$/i,
      (match) => {
        const groupName = match[1].trim();
        console.log(`[MapCommandHandler] Filtering to group: ${groupName}`);
        return {
          properties: {
            _action: 'filterToGroup',
            _actionTimestamp: Date.now(),
            _groupName: groupName,
          },
        };
      }
    );

    // Delete group/layer: "delete group Test", "delete layer Water Sensors"
    this.commandPatterns.set(
      /(?:delete|remove)\s+(?:group|layer)\s+["']?([^"']+)["']?$/i,
      (match) => {
        const groupName = match[1].trim();
        console.log(`[MapCommandHandler] Deleting group: ${groupName}`);
        return {
          properties: {
            _action: 'deleteGroup',
            _actionTimestamp: Date.now(),
            _groupName: groupName,
          },
        };
      }
    );

    // Show/hide layer/group panel: "show layer panel", "hide layer panel", "show group filter"
    this.commandPatterns.set(
      /(show|hide)\s+(?:group\s+(?:filter|panel)|layer\s+panel)/i,
      (match) => {
        const show = match[1].toLowerCase() === 'show';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} layer panel`);
        return {
          properties: {
            showGroupFilter: show,
            showLayerPanel: show,
          },
        };
      }
    );

    // Add pin to group: "add pin to Field A", "assign pin to group Sensors"
    this.commandPatterns.set(
      /(?:add|assign|move)\s+pin\s+to\s+(?:group\s+)?["']?([^"']+)["']?$/i,
      (match, component) => {
        const groupName = match[1].trim();
        const selectedPinId = this.getMapProps(component)?.selectedPinId;
        if (!selectedPinId) {
          console.log('[MapCommandHandler] No pin selected for group assignment');
          return null;
        }
        const pins = [...(this.getMapProps(component)?.pins ?? [])];
        const groups = [...(this.getMapProps(component)?.groups ?? [])];
        const group = groups.find((g: MapGroup) => g.name.toLowerCase() === groupName.toLowerCase());
        if (!group) {
          console.log(`[MapCommandHandler] Group not found: ${groupName}`);
          return null;
        }
        const pinIndex = pins.findIndex((p: MapPin) => p.id === selectedPinId);
        if (pinIndex < 0) return null;

        // Update pin's groupId
        pins[pinIndex] = { ...pins[pinIndex], groupId: group.id };
        // Add pin to group's pinIds
        const groupIndex = groups.indexOf(group);
        const existingPinIds = group.pinIds ?? [];
        if (!existingPinIds.includes(selectedPinId)) {
          groups[groupIndex] = { ...group, pinIds: [...existingPinIds, selectedPinId] };
        }

        console.log(`[MapCommandHandler] Added pin to group: ${groupName}`);
        return { properties: { pins, groups } };
      }
    );

    // Remove pin from group: "remove pin from Field A", "unassign pin from group Sensors"
    this.commandPatterns.set(
      /(?:remove|unassign)\s+pin\s+from\s+(?:group\s+)?["']?([^"']+)["']?$/i,
      (match, component) => {
        const groupName = match[1].trim();
        const selectedPinId = this.getMapProps(component)?.selectedPinId;
        if (!selectedPinId) {
          console.log('[MapCommandHandler] No pin selected for group removal');
          return null;
        }
        const pins = [...(this.getMapProps(component)?.pins ?? [])];
        const groups = [...(this.getMapProps(component)?.groups ?? [])];
        const group = groups.find((g: MapGroup) => g.name.toLowerCase() === groupName.toLowerCase());
        if (!group) {
          console.log(`[MapCommandHandler] Group not found: ${groupName}`);
          return null;
        }
        const pinIndex = pins.findIndex((p: MapPin) => p.id === selectedPinId);
        if (pinIndex < 0) return null;

        // Clear pin's groupId
        pins[pinIndex] = { ...pins[pinIndex], groupId: undefined };
        // Remove pin from group's pinIds
        const groupIndex = groups.indexOf(group);
        groups[groupIndex] = {
          ...group,
          pinIds: (group.pinIds ?? []).filter((id: string) => id !== selectedPinId),
        };

        console.log(`[MapCommandHandler] Removed pin from group: ${groupName}`);
        return { properties: { pins, groups } };
      }
    );

    // ========================================================================
    // Phase 5 (Groups/Pins): Pin Management Patterns
    // ========================================================================

    // Enter pin placement mode: "place pin on map", "add pin", "drop pin"
    this.commandPatterns.set(
      /(?:place|add|drop|create)\s+(?:a\s+)?pin(?:\s+on\s+(?:the\s+)?map)?$/i,
      () => {
        console.log("[MapCommandHandler] Entering pin placement mode");
        return {
          properties: {
            pinPlacementMode: true,
          },
        };
      }
    );

    // Place pin at coordinates: "add pin at 40.7, -74.0", "place pin at 35.6, 139.7"
    this.commandPatterns.set(
      /(?:place|add|drop|create)\s+(?:a\s+)?pin\s+at\s+(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/i,
      (match) => {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        console.log(`[MapCommandHandler] Creating pin at coordinates: [${lat}, ${lng}]`);
        return {
          properties: {
            _action: 'createPinAtCoordinates',
            _actionTimestamp: Date.now(),
            _pinCoordinates: [lng, lat],
          },
        };
      }
    );

    // Place pin with name: "place pin named Sensor A", "add pin called Main Office"
    this.commandPatterns.set(
      /(?:place|add|drop|create)\s+(?:a\s+)?pin\s+(?:named?|called)\s+["']?([^"']+)["']?$/i,
      (match) => {
        const pinName = match[1].trim();
        console.log(`[MapCommandHandler] Entering pin placement mode with name: ${pinName}`);
        return {
          properties: {
            pinPlacementMode: true,
            pendingPin: {
              name: pinName,
            },
          },
        };
      }
    );

    // Set pin color: "make pin red", "set pin color blue", "pin color green", "set pin color to #FF5733"
    this.commandPatterns.set(
      /(?:make|set)?\s*pin\s+(?:color\s+)?(?:to\s+)?(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|#[0-9A-Fa-f]{3,6})/i,
      (match, component) => {
        const color = match[1].toLowerCase();
        const colorMap: Record<string, string> = {
          red: '#EF4444',
          blue: '#3B82F6',
          green: '#22C55E',
          yellow: '#EAB308',
          orange: '#F97316',
          purple: '#A855F7',
          pink: '#EC4899',
          black: '#000000',
          white: '#FFFFFF',
          gray: '#6B7280',
          grey: '#6B7280',
        };
        const hexColor = colorMap[color] || color;
        console.log(`[MapCommandHandler] Setting pin color to ${color} (${hexColor})`);

        // If a pin is selected, update it directly; otherwise update pendingPin
        const selectedPinId = this.getMapProps(component)?.selectedPinId;
        if (selectedPinId) {
          const pins = [...(this.getMapProps(component)?.pins ?? [])];
          const pinIndex = pins.findIndex((p: MapPin) => p.id === selectedPinId);
          if (pinIndex >= 0) {
            pins[pinIndex] = {
              ...pins[pinIndex],
              style: { ...pins[pinIndex].style, color: hexColor },
            };
            return { properties: { pins } };
          }
        }
        return {
          properties: {
            pendingPin: {
              ...this.getMapProps(component)?.pendingPin,
              style: {
                ...this.getMapProps(component)?.pendingPin?.style,
                color: hexColor,
              },
            },
          },
        };
      }
    );

    // Rename pin: "rename pin to Sensor A", "name pin Main Office", "call pin HQ"
    this.commandPatterns.set(
      /(?:rename|name|call)\s+(?:this\s+)?pin\s+(?:to\s+)?["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const newName = match[1].trim();
        const selectedPinId = this.getMapProps(component)?.selectedPinId;
        if (selectedPinId) {
          const pins = [...(this.getMapProps(component)?.pins ?? [])];
          const pinIndex = pins.findIndex((p: MapPin) => p.id === selectedPinId);
          if (pinIndex >= 0) {
            pins[pinIndex] = { ...pins[pinIndex], name: newName };
            return { properties: { pins } };
          }
        }
        return {
          properties: {
            pendingPin: { name: newName },
          },
        };
      }
    );

    // Set pin icon: "use camera icon", "set pin icon to sensor", "use icon home"
    this.commandPatterns.set(
      /(?:use|set)?\s*(?:pin\s+)?(?:icon\s+(?:to\s+)?([a-z]+)|([a-z]+)\s+icon)/i,
      (match, component) => {
        const requestedIcon = match[1] || match[2];
        const icon = resolveMapPinIcon(requestedIcon);
        if (!icon) {
          return null;
        }
        console.log(`[MapCommandHandler] Setting pin icon to ${icon}`);

        // If a pin is selected, update it directly; otherwise update pendingPin
        const selectedPinId = this.getMapProps(component)?.selectedPinId;
        if (selectedPinId) {
          const pins = [...(this.getMapProps(component)?.pins ?? [])];
          const pinIndex = pins.findIndex((p: MapPin) => p.id === selectedPinId);
          if (pinIndex >= 0) {
            pins[pinIndex] = {
              ...pins[pinIndex],
              style: { ...pins[pinIndex].style, icon },
            };
            return { properties: { pins } };
          }
        }
        return {
          properties: {
            pendingPin: {
              ...this.getMapProps(component)?.pendingPin,
              style: {
                ...this.getMapProps(component)?.pendingPin?.style,
                icon: icon,
              },
            },
          },
        };
      }
    );

    // Exit pin placement mode: "cancel pin", "exit pin mode", "stop placing pins"
    this.commandPatterns.set(
      /(?:cancel|exit|stop)\s+(?:pin(?:\s+(?:mode|placement))?|placing\s+pins?)/i,
      () => {
        console.log("[MapCommandHandler] Exiting pin placement mode");
        return {
          properties: {
            pinPlacementMode: false,
            pendingPin: undefined,
          },
        };
      }
    );

    // Show/hide pins: "show pins", "hide pins", "display pins"
    this.commandPatterns.set(
      /(show|hide|display)\s+(?:all\s+)?pins?$/i,
      (match) => {
        const show = match[1].toLowerCase() !== 'hide';
        console.log(`[MapCommandHandler] ${show ? 'Showing' : 'Hiding'} pins`);
        return {
          properties: {
            showPins: show,
          },
        };
      }
    );

    // Delete pin: "delete pin Sensor A", "remove pin Main Office"
    this.commandPatterns.set(
      /(?:delete|remove)\s+pin\s+["']?([^"']+)["']?$/i,
      (match) => {
        const pinName = match[1].trim();
        console.log(`[MapCommandHandler] Deleting pin: ${pinName}`);
        return {
          properties: {
            _action: 'deletePin',
            _actionTimestamp: Date.now(),
            _pinName: pinName,
          },
        };
      }
    );

    // ========================================================================
    // Phase 5 (Groups/Pins): Pin Navigation Patterns
    // ========================================================================

    // Link pin to page: "link pin to Dashboard page", "connect pin to Overview"
    this.commandPatterns.set(
      /(?:link|connect)\s+pin\s+(?:to\s+)?["']?([^"']+)["']?\s*(?:page)?$/i,
      (match) => {
        const pageName = match[1].trim();
        console.log(`[MapCommandHandler] Linking pin to page: ${pageName}`);
        return {
          properties: {
            _action: 'linkPinToPage',
            _actionTimestamp: Date.now(),
            _targetPage: pageName,
          },
        };
      }
    );

    // Enable pin navigation: "enable pin navigation", "turn on pin clicks"
    this.commandPatterns.set(
      /(?:enable|turn\s+on|activate)\s+pin\s+(?:navigation|clicks?|linking)/i,
      (match, component) => {
        console.log("[MapCommandHandler] Enabling pin navigation");
        return {
          properties: {
            pinNavigation: {
              ...this.getMapProps(component)?.pinNavigation,
              enabled: true,
            },
          },
        };
      }
    );

    // Disable pin navigation: "disable pin navigation", "turn off pin clicks"
    this.commandPatterns.set(
      /(?:disable|turn\s+off|deactivate)\s+pin\s+(?:navigation|clicks?|linking)/i,
      (match, component) => {
        console.log("[MapCommandHandler] Disabling pin navigation");
        return {
          properties: {
            pinNavigation: {
              ...this.getMapProps(component)?.pinNavigation,
              enabled: false,
            },
          },
        };
      }
    );

    // Make pins clickable/non-clickable: "make pins clickable", "make pins not clickable"
    this.commandPatterns.set(
      /make\s+pins?\s+(not\s+)?clickable/i,
      (match, component) => {
        const clickable = !match[1];
        console.log(`[MapCommandHandler] Making pins ${clickable ? 'clickable' : 'not clickable'}`);
        return {
          properties: {
            pinNavigation: {
              ...this.getMapProps(component)?.pinNavigation,
              enabled: clickable,
            },
          },
        };
      }
    );

    logger.debug("MapCommandHandler layer/group and pin patterns initialized", {
      layers: "create, show/hide, filter, delete",
      pins: "place, coordinates, name, color, icon, show/hide, delete",
      navigation: "link to page, enable/disable",
    });

    // ========================================================================
    // Phase 2: Container Sizing Patterns (Height, Width, MinHeight, AspectRatio)
    // ========================================================================

    // Set height with value: "set height 400px", "height 50%", "map height 350px"
    this.commandPatterns.set(
      /(?:set\s+)?(?:map\s+)?height\s+(?:to\s+)?(\d+(?:\.\d+)?)(px|%|vh|rem)?/i,
      (match) => {
        const value = parseFloat(match[1]);
        const unit = match[2] || 'px';
        const height = `${value}${unit}`;
        console.log(`[MapCommandHandler Phase 2] Setting container height to ${height}`);
        return { style: { height } };
      }
    );

    // Make map taller/shorter/bigger/smaller
    this.commandPatterns.set(
      /make\s+(?:the\s+)?map\s+(taller|shorter|bigger|smaller)/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const currentHeight = parseInt(this.getStyleString(component, 'height') ?? '', 10) || 350;
        const delta = ['taller', 'bigger'].includes(action) ? 50 : -50;
        const newHeight = Math.max(150, currentHeight + delta);
        console.log(`[MapCommandHandler Phase 2] Adjusting height from ${currentHeight}px to ${newHeight}px`);
        return { style: { height: `${newHeight}px` } };
      }
    );

    // Set width with value: "set width 100%", "width 800px", "map width 50%"
    this.commandPatterns.set(
      /(?:set\s+)?(?:map\s+)?width\s+(?:to\s+)?(\d+(?:\.\d+)?)(px|%|vw|rem)?/i,
      (match) => {
        const value = parseFloat(match[1]);
        const unit = match[2] || 'px';
        const width = `${value}${unit}`;
        console.log(`[MapCommandHandler Phase 2] Setting container width to ${width}`);
        return { style: { width } };
      }
    );

    // Full width: "full width", "make map full width"
    this.commandPatterns.set(
      /(?:make\s+)?(?:the\s+)?(?:map\s+)?full\s*width/i,
      () => {
        console.log("[MapCommandHandler Phase 2] Setting container to full width");
        return { style: { width: '100%' } };
      }
    );

    // Set min height: "set min height 300px", "minimum height 250px"
    this.commandPatterns.set(
      /(?:set\s+)?min(?:imum)?\s+height\s+(?:to\s+)?(\d+)(px|vh|%)?/i,
      (match) => {
        const value = parseInt(match[1], 10);
        const unit = match[2] || 'px';
        console.log(`[MapCommandHandler Phase 2] Setting container min height to ${value}${unit}`);
        return { style: { minHeight: `${value}${unit}` } };
      }
    );

    // Set max height: "set max height 600px", "maximum height 500px"
    this.commandPatterns.set(
      /(?:set\s+)?max(?:imum)?\s+height\s+(?:to\s+)?(\d+)(px|vh|%)?/i,
      (match) => {
        const value = parseInt(match[1], 10);
        const unit = match[2] || 'px';
        console.log(`[MapCommandHandler Phase 2] Setting container max height to ${value}${unit}`);
        return { style: { maxHeight: `${value}${unit}` } };
      }
    );

    // Aspect ratio with colon: "16:9 ratio", "4:3 aspect ratio", "set ratio to 16:9"
    this.commandPatterns.set(
      /(?:set\s+)?(?:aspect\s+)?ratio\s+(?:to\s+)?(\d+):(\d+)/i,
      (match) => {
        const width = parseInt(match[1], 10);
        const height = parseInt(match[2], 10);
        console.log(`[MapCommandHandler Phase 2] Setting aspect ratio to ${width}/${height}`);
        return { style: { aspectRatio: `${width}/${height}` } };
      }
    );

    // Aspect ratio prefix: "16:9", "4:3", "1:1" (standalone)
    this.commandPatterns.set(
      /^(\d+):(\d+)(?:\s+(?:ratio|aspect))?$/i,
      (match) => {
        const width = parseInt(match[1], 10);
        const height = parseInt(match[2], 10);
        console.log(`[MapCommandHandler Phase 2] Setting aspect ratio to ${width}/${height}`);
        return { style: { aspectRatio: `${width}/${height}` } };
      }
    );

    // Square map: "make map square", "square map"
    this.commandPatterns.set(
      /(?:make\s+)?(?:the\s+)?map\s+square|square\s+map/i,
      () => {
        console.log("[MapCommandHandler Phase 2] Setting map to square aspect ratio");
        return { style: { aspectRatio: '1/1' } };
      }
    );

    // Widescreen map: "widescreen", "widescreen map"
    this.commandPatterns.set(
      /(?:make\s+)?(?:the\s+)?(?:map\s+)?widescreen/i,
      () => {
        console.log("[MapCommandHandler Phase 2] Setting map to widescreen (16:9) aspect ratio");
        return { style: { aspectRatio: '16/9' } };
      }
    );

    // Clear aspect ratio: "clear aspect ratio", "remove ratio"
    this.commandPatterns.set(
      /(?:clear|remove|reset)\s+(?:aspect\s+)?ratio/i,
      () => {
        console.log("[MapCommandHandler Phase 2] Clearing aspect ratio");
        return { style: { aspectRatio: undefined } };
      }
    );

    logger.debug("MapCommandHandler container sizing patterns initialized", {
      height: "set height, taller/shorter",
      width: "set width, full width",
      minMaxHeight: "set min/max height",
      aspectRatio: "X:Y ratio, square, widescreen",
    });
    logger.debug("MapCommandHandler patterns initialized");
  }

  /**
   * Check if this handler can process the given command for the component
   */
  canHandle(command: string, component: Component): boolean {
    const isMapChart = component.type === ComponentType.MAP_CHART;
    const hasMapKeywords = this.hasMapKeywords(command);

    if (isMapChart && hasMapKeywords) {
      console.log(`[MapCommandHandler] Can handle: "${command}" for ${component.type}`);
    }

    return isMapChart && hasMapKeywords;
  }

  /**
   * Check if command contains map-related keywords
   */
  private hasMapKeywords(command: string): boolean {
    const mapKeywords = [
      // View control
      'zoom', 'pan', 'center', 'fit', 'bounds', 'view', 'pitch', 'bearing', 'rotation',
      // Layers
      'heatmap', 'heat map', 'marker', 'cluster', 'zone', 'layer',
      // Data/Sensors
      'sensor', 'moisture', 'dry', 'wet', 'saturated', 'field', 'data',
      // Map terms
      'map', 'geographic', 'gps', 'location', 'coordinate',
      // Themes
      'thermal', 'agricultural', 'terrain', 'ocean', 'grayscale',
      // Actions
      'show', 'hide', 'toggle', 'enable', 'disable', 'filter',
      // Display
      'legend', 'tooltip', 'controls', 'title', 'refresh',
      // Styling
      'radius', 'opacity', 'intensity', 'theme', 'color',
      // Tile providers
      'maptiler', 'stadia', 'osm', 'tiles',
      // Map styles
      'satellite', 'streets', 'hybrid', 'street',
      // Real-time (Phase 4)
      'real-time', 'realtime', 'live', 'pause', 'resume', 'update', 'connection', 'status',
      // Controls & Legend (Phase 5)
      'fullscreen', 'compass', 'scale', 'expand', 'collapse', 'compact', 'position', 'move',
      // Container Sizing (Phase 2)
      'height', 'width', 'taller', 'shorter', 'bigger', 'smaller', 'aspect', 'ratio', 'square', 'widescreen',
      // Responsive Breakpoints (Phase 6)
      'mobile', 'tablet', 'desktop',
      // Layers, Groups & Pins (Phase 5 Groups/Pins)
      'group', 'groups', 'layers', 'pin', 'pins', 'place', 'placement', 'drop',
      'marker', 'navigation', 'link', 'navigate', 'clickable', 'icon',
      // Location Lock
      'location', 'lock', 'unlock', 'save',
    ];

    const lowerCommand = command.toLowerCase();
    return mapKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  /**
   * Parse a command and return property changes
   */
  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    console.log(`[MapCommandHandler] Parsing command: "${command}" for ${component.type}`);

    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          // Deep merge properties
          changes.properties = this.deepMergeProps(changes.properties || {}, patternMatch.properties);
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }
    }

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 || Object.keys(changes.style || {}).length > 0) {
      console.log(`[MapCommandHandler] Successfully parsed command with changes:`, changes);
      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    console.log(`[MapCommandHandler] Could not parse command: "${command}"`);
    return {
      success: false,
      error: "Could not understand the map command",
      suggestions: await this.getSuggestions(component),
    };
  }

  /**
   * Deep merge helper for nested properties
   */


  /**
   * Get example commands for the map component
   */
  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    if (componentType !== ComponentType.MAP_CHART) return [];

    return [
      // View control
      'Zoom in',
      'Zoom out',
      'Zoom to 15',
      'Center at 40.7, -74.0',
      'Fit to all sensors',
      'Set pitch to 45',
      'Reset view',
      // Layers
      'Show heatmap',
      'Hide markers',
      'Enable clustering',
      'Show zones',
      'Switch to markers',
      // Styling
      'Use agricultural theme',
      'Apply thermal colors',
      'Set radius to 30',
      'Set opacity to 70%',
      'Make more transparent',
      'Set marker size to large',
      // Filtering
      'Filter moisture 20 to 60',
      'Show dry areas',
      'Show wet sensors',
      'Filter to zone A',
      'Clear filters',
      // Display
      'Set title to "North Field Map"',
      'Show legend',
      'Enable tooltips',
      'Hide controls',
      // Real-time (Phase 4)
      'Enable real-time',
      'Disable live updates',
      'Refresh every 30 seconds',
      'Pause updates',
      'Resume updates',
      'Show connection status',
      'Refresh data',
      // Controls & Legend (Phase 5)
      'Enter fullscreen',
      'Exit fullscreen',
      'Move controls to top-left',
      'Move legend to top-right',
      'Collapse legend',
      'Expand legend',
      'Show compass',
      'Hide scale',
      'Enable compact mode',
      'Show layer toggles',
      'Hide sensor count',
      // Container Sizing (Phase 2)
      'Set height 400px',
      'Set height 50%',
      'Make map taller',
      'Make map shorter',
      'Set width 100%',
      'Full width',
      'Set min height 300px',
      '16:9 ratio',
      '4:3 aspect ratio',
      'Make map square',
      'Widescreen',
      // Responsive Breakpoints (Phase 6)
      'On mobile set height 200px',
      'On tablet set height 300px',
      'On desktop zoom to 12',
      'For mobile hide legend',
      'Mobile: hide controls',
      // Layers (Phase 5 Groups/Pins)
      'Create layer Water Sensors',
      'Create moisture sensor layer',
      'Create area layer North Field',
      'Show layer Water Sensors',
      'Hide layer Test',
      'Filter to layer North',
      'Delete layer Test',
      'Show layer panel',
      'Hide layer panel',
      // Backward compat: "group" still works
      'Create group Field A',
      // Pins (Phase 5 Groups/Pins)
      'Place pin on map',
      'Add pin at 40.7, -74.0',
      'Place pin named Sensor A',
      'Make pin red',
      'Use flag icon',
      'Cancel pin',
      'Show pins',
      'Hide pins',
      'Delete pin Sensor A',
      // Pin Navigation (Phase 5 Groups/Pins)
      'Link pin to Dashboard page',
      'Enable pin navigation',
      'Disable pin navigation',
      'Make pins clickable',
      // Location Lock
      'Set location',
      'Lock location',
      'Save this view',
      'Unlock location',
      'Resume auto sync',
    ];
  }

  /**
   * Get contextual suggestions based on component state
   */
  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = this.getMapProps(component);

    // Layer suggestions
    const heatmapEnabled = props?.layers?.heatmap?.enabled !== false;
    const markersEnabled = props?.layers?.markers?.enabled === true;
    const clusteringEnabled = props?.enableClustering === true;

    if (heatmapEnabled) {
      suggestions.push('Hide heatmap', 'Switch to markers');
    } else {
      suggestions.push('Show heatmap');
    }

    if (!markersEnabled) {
      suggestions.push('Show markers');
    }

    if (!clusteringEnabled) {
      suggestions.push('Enable clustering');
    }

    // Theme suggestions
    const currentTheme = props?.colorTheme || 'agricultural';
    if (currentTheme !== 'thermal') {
      suggestions.push('Use thermal theme');
    }
    if (currentTheme !== 'ocean') {
      suggestions.push('Use ocean theme');
    }

    // View suggestions
    suggestions.push('Zoom in', 'Fit to all sensors');

    // Filter suggestions
    if (props?.moistureRange) {
      suggestions.push('Clear filters', 'Show all sensors');
    } else {
      suggestions.push('Show dry areas', 'Show wet sensors', 'Filter to zone A');
    }

    // Display suggestions
    if (!props?.showLegend) {
      suggestions.push('Show legend');
    }
    if (!props?.showTooltip) {
      suggestions.push('Enable tooltips');
    }

    // Title suggestion
    if (!props?.title) {
      suggestions.push('Set title to "Field Map"');
    }

    // Real-time suggestions (Phase 4)
    if (!props?.enableRealTime) {
      suggestions.push('Enable real-time');
    } else {
      suggestions.push('Pause updates', 'Disable live updates');
    }

    if (!props?.refreshInterval || props.refreshInterval === 0) {
      suggestions.push('Refresh every 30 seconds');
    } else {
      suggestions.push('Stop auto-refresh');
    }

    // Controls & Legend suggestions (Phase 5)
    if (props?.controlsConfig?.compact) {
      suggestions.push('Disable compact mode');
    } else {
      suggestions.push('Enable compact mode');
    }

    if (props?.legendConfig?.expanded !== false) {
      suggestions.push('Collapse legend');
    } else {
      suggestions.push('Expand legend');
    }

    suggestions.push('Enter fullscreen', 'Move legend to top-right');

    // Container Sizing suggestions (Phase 2)
    const currentHeight = this.getStyleString(component, 'height');
    if (!currentHeight) {
      suggestions.push('Set height 400px');
    } else {
      suggestions.push('Make map taller', 'Make map shorter');
    }
    suggestions.push('16:9 ratio', 'Full width');

    // Responsive suggestions (Phase 6)
    suggestions.push('On mobile set height 200px', 'On desktop zoom to 12');

    // Location Lock suggestions
    const isLocationLocked = props?.locationLocked === true;
    if (isLocationLocked) {
      suggestions.push('Unlock location', 'Resume auto sync');
    } else {
      suggestions.push('Set location', 'Lock location');
    }

    // Groups & Pins suggestions (Phase 5 Groups/Pins)
    const hasGroups = props?.groups && Array.isArray(props.groups) && props.groups.length > 0;
    const hasPins = props?.pins && Array.isArray(props.pins) && props.pins.length > 0;
    const inPinPlacementMode = props?.pinPlacementMode === true;
    const pinsVisible = props?.showPins !== false;
    const groupFilterVisible = props?.showLayerPanel === true || props?.showGroupFilter === true;
    const pinNavigationEnabled = props?.pinNavigation?.enabled === true;

    // Layer (group) suggestions
    if (hasGroups) {
      suggestions.push('Filter to layer Field A', 'Show layer panel');
    } else {
      suggestions.push('Create layer Field A');
    }

    if (groupFilterVisible) {
      suggestions.push('Hide layer panel');
    }

    // Pin suggestions
    if (inPinPlacementMode) {
      suggestions.push('Cancel pin', 'Make pin red', 'Use flag icon');
    } else {
      suggestions.push('Place pin on map');
    }

    if (hasPins) {
      if (pinsVisible) {
        suggestions.push('Hide pins');
      } else {
        suggestions.push('Show pins');
      }
      if (!pinNavigationEnabled) {
        suggestions.push('Enable pin navigation');
      } else {
        suggestions.push('Disable pin navigation', 'Link pin to Dashboard');
      }
    }

    return suggestions.slice(0, 12);
  }
}

// Export singleton instance
export const mapCommandHandler = new MapCommandHandler();
